import React, { useState, useEffect } from "react";
import { Grid, TextField, Autocomplete, CircularProgress, Box, IconButton, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import { accountTypes, accountStatus, indianBanks } from "../../data/bankData";
import countriesData from "../../data/countries";
import { CITIES } from "../../data/cities";
import CryptoJS from "crypto-js";
import axios from "axios";
import { extractAiTextFromImage } from "../../services/aiOcrService";
import { parseKeyValueText, firstFrom } from "../../services/ocrUtils";

// Encryption/Decryption Key - In production, this should be stored securely
const ENCRYPTION_KEY = "MoonaConsultancySecretKey";

/**
 * Encrypt sensitive data
 * @param {string} text - Text to encrypt
 * @returns {string} - Encrypted text
 */
export const encryptData = (text) => {
  if (!text || text.trim() === "") return "";
  try {
    return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    return text;
  }
};

/**
 * Decrypt sensitive data
 * @param {string} encryptedText - Encrypted text to decrypt
 * @returns {string} - Decrypted text
 */
export const decryptData = (encryptedText) => {
  if (!encryptedText || encryptedText.trim() === "") return "";
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return decrypted || encryptedText;
  } catch (error) {
    console.error("Decryption error:", error);
    return encryptedText;
  }
};

const BankDetailsFields = ({
  formData,
  handleChange,
  section = 1,
  textInputStyle = {},
  fieldErrors = {},
}) => {
  const [allBanks, setAllBanks] = useState(indianBanks);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [bankDataFetched, setBankDataFetched] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);

  const accountHolderNameError = fieldErrors[`accountholdername${section}`] || "";
  const accountNumberError = fieldErrors[`accountnumber${section}`] || "";
  const ifscError = fieldErrors[`ifsc${section}`] || "";
  const accountTypeError = fieldErrors[`accounttype${section}`] || "";
  const bankNameError = fieldErrors[`bankname${section}`] || "";

  // Helper to handle encrypted field changes
  const handleEncryptedChange = (fieldName, value) => {
    handleChange({ target: { name: fieldName, value } });
  };

  // Fetch bank list from API on mount
  useEffect(() => {
    const fetchAllBanks = async () => {
      if (bankDataFetched) return; // Only fetch once
      
      setLoadingBanks(true);
      try {
        
        // Method 1: Try fetching from bank-codes API (GitHub raw content)
        // This provides a comprehensive list of Indian banks
        try {
          const response = await axios.get(
            'https://raw.githubusercontent.com/razorpay/ifsc/master/src/banknames.json',
            { timeout: 10000 }
          );
          
          if (response.data && typeof response.data === 'object') {
            // The API returns an object with bank codes as keys and names as values
            const bankNames = Object.values(response.data).sort();
            
            if (bankNames.length > 0) {
              setAllBanks(bankNames);
              setBankDataFetched(true);
              setLoadingBanks(false);
              return;
            }
          }
        } catch (apiError) {
          console.warn("Primary API failed, trying alternative...", apiError.message);
        }

        // Method 2: Try alternative API - IFSC.codes
        try {
          const response = await axios.get(
            'https://ifsc.razorpay.com/',
            { timeout: 10000 }
          );
          
          // If we get a response, parse it to extract bank names
          if (response.data) {
          }
        } catch (apiError) {
          console.warn("Alternative API also failed:", apiError.message);
        }

        // Method 3: Fallback - use a curated static list from reliable source
        const fallbackBanks = [
          ...new Set([...indianBanks])
        ].sort();
        
        setAllBanks(fallbackBanks);
        setBankDataFetched(true);
        
        
      } catch (error) {
        console.error("Error fetching bank list:", error);
        // Ultimate fallback to static list
        const fallbackBanks = [...new Set([...indianBanks])].sort();
        setAllBanks(fallbackBanks);
      } finally {
        setLoadingBanks(false);
        setBankDataFetched(true);
      }
    };

    fetchAllBanks();
  }, [bankDataFetched]);



  // Fetch branch details based on IFSC code
  const fetchBranchFromIFSC = async (ifscCode) => {
    if (!ifscCode || ifscCode.length < 11) return;

    setLoadingBranches(true);
    try {
      const response = await axios.get(`https://ifsc.razorpay.com/${ifscCode}`);
      if (response.data) {
        const { BANK, BRANCH, CITY, STATE } = response.data;
        
        // Auto-populate bank name if not already set
        if (BANK && !formData[`bankname${section}`]) {
          handleChange({ target: { name: `bankname${section}`, value: BANK } });
        }
        
        // Auto-populate branch if not already set
        if (BRANCH && !formData[`branch${section}`]) {
          handleChange({ target: { name: `branch${section}`, value: BRANCH } });
        }
      }
    } catch (error) {
      console.error("Error fetching branch details:", error);
    } finally {
      setLoadingBranches(false);
    }
  };

  // Watch for IFSC code changes to auto-populate bank and branch
  useEffect(() => {
    const ifscCode = formData[`ifsc${section}`];
    if (ifscCode && ifscCode.length === 11) {
      fetchBranchFromIFSC(ifscCode);
    }
  }, [formData[`ifsc${section}`]]);

  return (
    <Grid container spacing={3}>
      {/* Upload Image - moved to top */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Passbook Image
        </label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id={`passbook-image-upload-${section}`}
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  handleChange({ target: { name: `image${section}`, value: reader.result } });
                };
                reader.readAsDataURL(file);
                // Run OCR to auto-fill bank fields
                setOcrBusy(true);
                (async () => {
                  try {
                    const text = await extractAiTextFromImage(file);
                    const kv = parseKeyValueText(text);
                    const updates = {
                      [`accountholdername${section}`]: firstFrom(kv, ["accountholdername", "name", "holdername"]),
                      [`accountnumber${section}`]: firstFrom(kv, ["accountnumber", "acno", "accountno", "a\u002Fcno", "a\\cno"]),
                      [`ifsc${section}`]: firstFrom(kv, ["ifsc", "ifsccode", "ifsc_code"]).toUpperCase(),
                      [`bankname${section}`]: firstFrom(kv, ["bankname", "bank"]),
                      [`branch${section}`]: firstFrom(kv, ["branch", "branchname"]),
                    };
                    for (const [name, value] of Object.entries(updates)) {
                      if (value) handleChange({ target: { name, value } });
                    }
                  } catch (e) {
                    console.warn("BankDetailsFields OCR failed:", e?.message || e);
                  } finally {
                    setOcrBusy(false);
                  }
                })();
              }
            }}
          />
          <label htmlFor={`passbook-image-upload-${section}`}>
            <MDButton
              variant="outlined"
              color="info"
              component="span"
              size="small"
              startIcon={<Icon>upload</Icon>}
            >
              Upload Passbook
            </MDButton>
          </label>
          <Typography variant="caption" color="text.secondary">
            {ocrBusy ? "Reading text to auto-fill…" : "Upload image to auto fill"}
          </Typography>
          {formData[`image${section}`] && (
            <Box
              sx={{
                position: "relative",
                mt: 2,
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Preview:
              </Typography>
              <Box
                component="img"
                src={formData[`image${section}`]}
                alt="Passbook preview"
                sx={{
                  width: "100%",
                  maxHeight: 150,
                  objectFit: "contain",
                  borderRadius: "4px",
                  backgroundColor: "white",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: 1,
                  "&:hover": { backgroundColor: "white" },
                }}
                onClick={() => {
                  handleChange({ target: { name: `image${section}`, value: "" } });
                  const input = document.getElementById(`passbook-image-upload-${section}`);
                  if (input) {
                    input.value = "";
                  }
                }}
              >
                <Icon fontSize="small" color="error">close</Icon>
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>
      {/* Bank Name */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Bank Name <span className="text-red-500">*</span>
        </label>
        <Autocomplete
          freeSolo
          options={allBanks}
          loading={loadingBanks}
          value={formData[`bankname${section}`] || ""}
          onChange={(e, value) => {
            handleChange({ target: { name: `bankname${section}`, value } });
          }}
          // Enable filtering
          autoComplete
          autoHighlight
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          // Show dropdown icon
          forcePopupIcon={true}
          // Custom rendering for better UX
          renderOption={(props, option, state) => (
            <li {...props} key={`${option}-${state.index}`}>
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Type to search bank name"
              sx={textInputStyle}
              error={!!bankNameError}
              helperText={bankNameError || ""}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingBanks ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>

      {/* Account Holder Name */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Account Holder Name <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`accountholdername${section}`}
          value={formData[`accountholdername${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter account holder name"
          sx={textInputStyle}
          error={!!accountHolderNameError}
          helperText={accountHolderNameError}
        />
      </Grid>

      {/* Account Number (Encrypted) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Account Number <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`accountnumber${section}`}
          value={formData[`accountnumber${section}`] || ""}
          onChange={(e) => handleEncryptedChange(`accountnumber${section}`, e.target.value)}
          variant="outlined"
          placeholder="Enter account number"
          sx={textInputStyle}
          error={!!accountNumberError}
          helperText={accountNumberError}
        />
      </Grid>

      {/* IFSC Code (Encrypted) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          IFSC Code <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`ifsc${section}`}
          value={formData[`ifsc${section}`] || ""}
          onChange={(e) => {
            // Convert IFSC to uppercase before storing
            const uppercaseValue = e.target.value.toUpperCase();
            handleEncryptedChange(`ifsc${section}`, uppercaseValue);
          }}
          variant="outlined"
          placeholder="Enter IFSC code (e.g., SBIN0001234)"
          sx={textInputStyle}
          error={!!ifscError}
          helperText={
            loadingBranches
              ? "Fetching bank details..."
              : ifscError || "Bank name and branch will be auto-filled"
          }
          inputProps={{
            style: { textTransform: "uppercase" },
            maxLength: 11,
          }}
          InputProps={{
            endAdornment: loadingBranches ? (
              <CircularProgress color="inherit" size={20} />
            ) : null,
          }}
        />
      </Grid>

      {/* Account Type */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Account Type <span className="text-red-500">*</span>
        </label>
        <Autocomplete
          freeSolo
          options={accountTypes}
          value={formData[`accounttype${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `accounttype${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select account type"
              sx={textInputStyle}
              error={!!accountTypeError}
              helperText={accountTypeError}
            />
          )}
        />
      </Grid>

      {/* Branch */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Branch <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`branch${section}`}
          value={formData[`branch${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter branch name"
          sx={textInputStyle}
          helperText={loadingBranches ? "Fetching branch..." : "Auto-filled from IFSC code"}
          InputProps={{
            endAdornment: loadingBranches ? (
              <CircularProgress color="inherit" size={20} />
            ) : null,
          }}
        />
      </Grid>

      {/* Bank Country */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Bank Country
        </label>
        <Autocomplete
          freeSolo
          options={countriesData.map((c) => c.name)}
          value={formData[`bankcountry${section}`] || ""}
          onChange={(e, value) => {
            handleChange({ target: { name: `bankcountry${section}`, value } });
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select bank country" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* Status */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Account Status</label>
        <Autocomplete
          freeSolo
          options={accountStatus}
          value={formData[`status${section}`] || "Active"}
          onChange={(e, value) => handleChange({ target: { name: `status${section}`, value } })}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select account status" sx={textInputStyle} />
          )}
        />
      </Grid>


      {/* Additional Notes */}
      <Grid item xs={12}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
        <TextField
          fullWidth
          name={`bankdescription${section}`}
          value={formData[`bankdescription${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Any additional notes about this account"
          multiline
          rows={2}
          sx={textInputStyle}
        />
      </Grid>
    </Grid>
  );
};

export default BankDetailsFields;
